import { notifyInfo } from '@components/atoms/toast/services';
import { getAdminRemoteEntryUrl } from './admin-url';
import { notifyWarning } from '@components/atoms/toast/services';

export interface RemoteDebugInfo {
  url: string;
  accessible: boolean;
  cors: boolean | null;
  contentType: string | null;
  error?: string;
  hasInit: boolean;
  hasGet: boolean;
  size?: number;
}

export async function debugRemoteEntry(url: string): Promise<RemoteDebugInfo> {
  const result: RemoteDebugInfo = {
    url,
    accessible: false,
    cors: null,
    contentType: null,
    hasInit: false,
    hasGet: false,
  };

  try {
    const response = await fetch(url, { method: 'HEAD' });
    result.accessible = response.ok;
    result.contentType = response.headers.get('content-type');

    const corsHeader = response.type === 'cors';
    result.cors = corsHeader;
    if (result.accessible) {
      const contentResponse = await fetch(url);
      const content = await contentResponse.text();
      result.size = content.length;

      result.hasInit = content.includes('export { getExposes as get, init }') || content.includes('init');
      result.hasGet = content.includes('export { getExposes as get, init }') || content.includes('get');
    }
  } catch (error) {
    result.error = error instanceof Error ? error.message : 'Unknown error';
  }

  return result;
}

declare global {
  interface Window {
    __webpack_share_scopes__?: any;
  }
}

async function testRemoteInit(remote: any): Promise<void> {
  if (remote.init && typeof remote.init === 'function') {
    notifyInfo('🎯 Init function found, calling it...', 5000);
    try {
      const shareScope = window.__webpack_share_scopes__?.default || {};
      await remote.init(shareScope);
      notifyInfo('✅ Remote initialized successfully', 5000);
    } catch (initError) {
      notifyWarning('❌ Remote initialization failed', 5000);
    }
  } else {
    notifyWarning('⚠️ No init function found in remote', 5000);
  }
}

async function testRemoteGet(remote: any): Promise<void> {
  if (remote.get && typeof remote.get === 'function') {
    notifyInfo('🎯 Get function found', 5000);

    try {
      const contractForm = await remote.get('./ContractForm');
      notifyInfo('✅ ContractForm module loaded:', 5000);
    } catch (getError) {
      notifyWarning('❌ Failed to load ContractForm module', 5000);
    }
  } else {
    notifyWarning('⚠️ No get function found in remote', 5000);
  }
}

export async function testRemoteLoading(): Promise<void> {
  const remoteUrl = getAdminRemoteEntryUrl();

  notifyInfo('🔍 Testing remote loading...');

  const debugInfo = await debugRemoteEntry(remoteUrl);

  if (!debugInfo.accessible) {
    notifyWarning('❌ Remote is not accessible', 5000);
    return;
  }

  if (!debugInfo.cors) {
    notifyWarning('⚠️ CORS might be an issue', 5000);
  }

  try {
    notifyInfo('🚀 Attempting dynamic import...');
    const remote = await import(remoteUrl);
    notifyInfo('✅ Remote loaded successfully:', remote);

    await testRemoteInit(remote);
    await testRemoteGet(remote);
  } catch (importError) {
    notifyWarning('❌ Failed to import remote', 5000);
  }
}

(window as any).testRemoteLoading = testRemoteLoading;
(window as any).debugRemoteEntry = debugRemoteEntry;
